import { ProCard } from '@ant-design/pro-components'
import { FindingsAlert, FindingsSelector } from '@app/icare/components/findings'
import PatientTitle from '@app/icare/entities/patient/components/PatientTitle'
import { PatientStammdatenEditDrawer } from '@app/icare/entities/patient/edits/PatientStammdatenEditDrawer'
import { selectByGroup } from '@app/icare/entities/patient/queries/patientFindingsQueryOptions'
import { patientQueryOptions } from '@app/icare/entities/patient/queries/patientQueryOptions'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { entityParams } from '@pkg/db/utils'
import { formatAddress } from '@pkg/shared/utils'
import { EditSectionButton } from '@pkg/ui/button'
import MapLink from '@pkg/ui/links/MapLink'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Flex, Typography } from 'antd'
import { FaEnvelope, FaPhone } from 'react-icons/fa6'

export const Route = createFileRoute('/_app/patienten/$id/_edit/stammdaten/')({
  component: RouteComponent,
  params: entityParams({ id: PatientId }),
})

function RouteComponent() {
  const { id: patientId } = Route.useParams()
  const { data: patient } = useSuspenseQuery(patientQueryOptions(patientId))

  return (
    <Flex vertical gap="small">
      <FindingsSelector
        selector={selectByGroup('personal')}
        children={f => (
          <FindingsAlert
            findings={f}
            onFix={() => PatientStammdatenEditDrawer.show({ patientId, section: 'personal' })}
          />
        )}
      />
      <FindingsSelector
        selector={selectByGroup('address')}
        children={f => (
          <FindingsAlert
            findings={f}
            onFix={() => PatientStammdatenEditDrawer.show({ patientId, section: 'address' })}
          />
        )}
      />

      {/* Personal — already view-first */}
      <ProCard
        headerBordered
        title={<Trans>Persönliche Daten</Trans>}
        extra={
          <EditSectionButton
            aria-label={t`Persönliche Daten bearbeiten`}
            onClick={() => PatientStammdatenEditDrawer.show({ patientId, section: 'personal' })}
          >{t`Bearbeiten`}</EditSectionButton>
        }
      >
        <div className="flex items-baseline gap-2">
          <PatientTitle value={patient} />
        </div>
      </ProCard>

      {/* Contact */}
      <ProCard
        headerBordered
        title={<Trans>Kontaktinformationen</Trans>}
        extra={
          <EditSectionButton
            aria-label={t`Kontaktinformationen bearbeiten`}
            onClick={() => PatientStammdatenEditDrawer.show({ patientId, section: 'contact' })}
          >{t`Bearbeiten`}</EditSectionButton>
        }
      >
        {patient?.contact?.phone || patient?.contact?.mobile || patient?.contact?.email ? (
          <div className="flex flex-wrap gap-4 text-sm">
            {patient?.contact?.phone && (
              <span>
                <FaPhone className="inline mr-1.5 text-gray-400" />
                {patient.contact.phone}
              </span>
            )}
            {patient?.contact?.mobile && (
              <span>
                <FaPhone className="inline mr-1.5 text-gray-400" />
                {patient.contact.mobile}
              </span>
            )}
            {patient?.contact?.email && (
              <span>
                <FaEnvelope className="inline mr-1.5 text-gray-400" />
                {patient.contact.email}
              </span>
            )}
          </div>
        ) : (
          <Typography.Text type="secondary">{t`Keine Kontaktdaten`}</Typography.Text>
        )}
      </ProCard>

      {/* Address */}
      <ProCard
        headerBordered
        title={t`Standardanschrift`}
        extra={
          <div className="flex items-center gap-1">
            <MapLink value={patient?.address} />
            <EditSectionButton
              aria-label={t`Standardanschrift bearbeiten`}
              onClick={() => PatientStammdatenEditDrawer.show({ patientId, section: 'address' })}
            >{t`Bearbeiten`}</EditSectionButton>
          </div>
        }
      >
        <span className="text-sm">{formatAddress([], patient?.address, t`Keine Adresse`)}</span>
      </ProCard>

      {/* Comment */}
      <ProCard
        headerBordered
        title={<Trans>Notizen</Trans>}
        extra={
          <EditSectionButton
            aria-label={t`Notizen bearbeiten`}
            onClick={() => PatientStammdatenEditDrawer.show({ patientId, section: 'comment' })}
          >{t`Bearbeiten`}</EditSectionButton>
        }
      >
        {patient?.comment ? (
          <Typography.Paragraph className="mb-0!">{patient.comment}</Typography.Paragraph>
        ) : (
          <Typography.Text type="secondary">{t`Keine Notizen`}</Typography.Text>
        )}
      </ProCard>
    </Flex>
  )
}
